// frontend/src/components/PartnersTab.js
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import RichTextEditor from "@/components/RichTextEditor";
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { SortableItem } from "@/components/SortableItem";
import { ACCENT_OPTIONS } from "@/components/shared";

function PartnersTab({ config, onChange }) {
  const partners = config.partners || [];
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const deepClone = () => JSON.parse(JSON.stringify(config));

  const updatePartner = (index, field, value) => {
    const next = deepClone();
    next.partners[index][field] = value;
    onChange(next);
  };

  const addPartner = () => {
    const next = deepClone();
    next.partners.push({
      id: `partner-${Date.now()}`,
      name: "New Partner",
      linkText: "example.com",
      url: "https://example.com",
      description: "Description of the new partner.",
      accent: "teal",
    });
    onChange(next);
  };

  const deletePartner = (index) => {
    if (!window.confirm(`Delete "${partners[index].name}"?`)) return;
    const next = deepClone();
    next.partners.splice(index, 1);
    onChange(next);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = partners.map((p) => p.id);
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = deepClone();
    next.partners = arrayMove(next.partners, oldIndex, newIndex);
    onChange(next);
  };

  return (
    <div className="space-y-4">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={partners.map((p) => p.id)} strategy={verticalListSortingStrategy}>
          {partners.map((partner, index) => (
            <SortableItem key={partner.id} id={partner.id}>
              <div className="leadline-border overflow-hidden bg-[#F5EDE0]">
                <div
                  className="px-4 py-2 text-sm font-medium text-white"
                  style={{ backgroundColor: index === 0 ? config.colors.secondary : config.colors.primary }}
                >
                  {partner.name}
                </div>
                <div className="p-4 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <label className="block">
                      <span className="block text-xs mb-1 text-[#3A3530]">Name</span>
                      <input value={partner.name} onChange={(e) => updatePartner(index, "name", e.target.value)} className="w-full leadline-border-thin px-2 py-1 bg-white text-sm" />
                    </label>
                    <label className="block">
                      <span className="block text-xs mb-1 text-[#3A3530]">Link Text</span>
                      <input value={partner.linkText} onChange={(e) => updatePartner(index, "linkText", e.target.value)} className="w-full leadline-border-thin px-2 py-1 bg-white text-sm" />
                    </label>
                    <label className="block">
                      <span className="block text-xs mb-1 text-[#3A3530]">Accent Color</span>
                      <select value={partner.accent || "teal"} onChange={(e) => updatePartner(index, "accent", e.target.value)} className="w-full leadline-border-thin px-2 py-1 bg-white text-sm">
                        {ACCENT_OPTIONS.map((opt) => (
                          <option key={opt.key} value={opt.key}>{opt.label}</option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <label className="block">
                    <span className="block text-xs mb-1 text-[#3A3530]">URL</span>
                    <input value={partner.url} onChange={(e) => updatePartner(index, "url", e.target.value)} className="w-full leadline-border-thin px-2 py-1 bg-white text-sm" />
                  </label>
                  <div className="block">
                    <span className="block text-xs mb-1 text-[#3A3530]">Description</span>
                    <RichTextEditor value={partner.description || ""} onChange={(v) => updatePartner(index, "description", v)} minHeight="4rem" />
                  </div>
                  <button onClick={() => deletePartner(index)} className="text-xs text-red-600 hover:underline">Delete partner</button>
                </div>
              </div>
            </SortableItem>
          ))}
        </SortableContext>
      </DndContext>

      <button
        onClick={addPartner}
        className="leadline-border-thin px-3 py-1 text-xs bg-[#1E1E1E] text-[#F5EDE0] hover:opacity-80"
      >
        + Add Partner
      </button>
    </div>
  );
}

export default PartnersTab;
